import { Entity } from "src/app/core/data/schema/entity";
import { Feature } from "./cbs/feature";
import { Page } from "./cbs/page";
import { Utilities } from "src/app/core/helper/util";
import { ServiceHelper } from "src/app/core/helper/services.helper";
import { IDashResetPasswordRequest } from "src/app/core/modules/auth-core/data/schema/auth-interfaces";
import { CbsEvent, CbsEventType } from "./events/event";
import { EventRegistration } from "./events/event-registration";
import { AppSettings } from "src/app/core/configs/app.settings";
import { SurveyResponse } from "./surveys/survey-response";
import { UserMembership } from "./memberships/user-membership";
import { DateTimeHelper } from "src/app/core/helper/datetime.helper";

export enum CbsAccessLevel {
  Removed = 0,
  Waitlisted = 5,
  Guest = 10,
  Operations = 20,
  Admin = 30,
  Owner = 50,
}

export class CommunityUser extends Entity {

  public static className = 'CommunityUser';
  public static idField = 'communityUserId';

  id: number = 0;
  userId: number = 0;
  communityUserId: number = 0;
  firstName: string
  lastName: string;
  email: string;
  password: string;
  role: string;
  createdAt: string;
  updatedAt: string;
  membershipName: string;
  profileImgPath: string;
  ewalletBalance: number = 0;
  shippingAddress: string;
  contactAddress: string;

  accessLevel: CbsAccessLevel;
  communityVerified: boolean; // community override on email verification, set by admin
  contactEmail: string;
  contactPhone: string = ''; // need to init to blank to appease change checking
  // since phone input will convert null to blank

  interestTagIds: string;
  emailOptIn: boolean = false;
  smsOptIn: boolean = false;
  exportToken: string;
  exportData: string;

  // mapped in api
  emailVerified: boolean; // user has verified their email on AspNetUser record
  communityUrl: string;

  myMemberships: UserMembership[] = [];
  myRegistrations: EventRegistration[] = [];
  upcomingEvents: CbsEvent[] = [];
  recentNews: Page[] = [];
  featured: Feature[] = [];
  eventRegs: EventRegs[] = [];

  onboardingSurveyResponseId?: number;

  filterableTypes: string;
  accessLevelStr: string = '-'
  joinedAt: string;

  get name() {
    return (this.firstName ?? '') + ' ' + (this.lastName ?? '') + ' ' + (this.contactEmail ?? '');
  }

  get hasOwner() { return this.accessLevel >= CbsAccessLevel.Owner }
  get hasAdmin() { return this.accessLevel >= CbsAccessLevel.Admin }
  get hasOperations() { return this.accessLevel >= CbsAccessLevel.Operations }

  constructor(init?: Partial<CommunityUser>) {
    super('CommunityUser', 'communityUserId', 'communityUsers');
    Object.assign(this, init);

    if (this.myRegistrations != null) { this.myRegistrations = this.myRegistrations.map(m => new EventRegistration(m)); }
    if (this.upcomingEvents != null) { this.upcomingEvents = this.upcomingEvents.map(m => new CbsEvent(m)); }
    if (this.recentNews != null) { this.recentNews = this.recentNews.map(m => new Page(m)); }
    if (this.featured != null) { this.featured = this.featured.map(m => new Feature(m)); }
    if (this.eventRegs == null) { this.eventRegs = [] }; // to avoid having to null check later

    switch (this.accessLevel) {
      case CbsAccessLevel.Removed:
        this.filterableTypes = 'Banned';
        this.accessLevelStr = 'Banned';
        break;
      case CbsAccessLevel.Guest:
        this.filterableTypes = 'Active,Guest';
        this.accessLevelStr = 'Guest';
        break;
      case CbsAccessLevel.Operations:
        this.filterableTypes = 'Active,Operations';
        this.accessLevelStr = 'Operations';
        break;
      case CbsAccessLevel.Admin:
        this.filterableTypes = 'Active,Admin';
        this.accessLevelStr = 'Admin';
        break;
      case CbsAccessLevel.Owner:
        this.filterableTypes = 'Active,Owner';
        this.accessLevelStr = 'Owner';
        break;
    }

    this.parseSpots();


    this.joinedAt = DateTimeHelper.formatDateShort(this.createdAt);
  }


  hasChanges() {
    return CommunityUser.hasChanges(CommunityUser);
  }

  revert() {
    return CommunityUser.revert(CommunityUser);
  }

  public static async load(id: number) {
    return super.getById(CommunityUser, id, 'communityUsers', 'user');
  }

  // attempt to prevent double loading since multiple places on a page may attempt to load at once
  private static isLoadingMyProfile: boolean = false;
  private static _profile: CommunityUser;

  // this is for user management and change checking, need to be sure it doesn't conflict with _profile
  protected static _cachedServerObj: Page; // unmodified until saved
  protected static _cachedObj: Page; // working copy
  protected static _cacheExpiry: number;


  public static async clearCache() {
    CommunityUser._cachedObj = undefined;
    CommunityUser._cachedServerObj = undefined;
    CommunityUser._profile = undefined;
  }

  // fetch all users in current community, current community would be pulled from claim
  public static async reloadMyProfile() {
    CommunityUser._profile = undefined;
    return CommunityUser.myProfile(true);
  }

  public static async exportUsers() {
    return await this.fetchUntyped('Exporting Users', 'communityusers', 'export', 'text') as string;
  }
  
  public static async myProfile(reload?: boolean) {
    //Log.debug('getUserCommunityProfile...')

    while (CommunityUser.isLoadingMyProfile) {
      await Utilities.wait(100);
    }

    CommunityUser.isLoadingMyProfile = true;
    try {
      if (CommunityUser._profile == null || reload) {
        // need to assign to new CommunityUser object to prevent conflict with managed users
        CommunityUser._profile = new CommunityUser(await super.getObject(CommunityUser, 'communities', `getUserCommunityProfile`));
      }
      CommunityUser.isLoadingMyProfile = false;

      // gonna blow these away to avoid change check issues
      CommunityUser._cachedObj = undefined;
      CommunityUser._cachedServerObj = undefined;
      return CommunityUser._profile;
    } catch (error) {
      console.error('error loading community profile', error)
      throw error;
    }
    finally {
      CommunityUser.isLoadingMyProfile = false;
    }
  }


  public static async resetPassword(request: IDashResetPasswordRequest) {
    try {
      return ServiceHelper.httpSvc.post(request, `${AppSettings.apiURL}/communityusers/adminpasswordreset`, 'text');
    } catch (error) {
      throw error || 'Error resetting password.';
    }
  }

  public static async fetchMyCommunityProfiles() {
    return super.fetchArray(CommunityUser, 'communityUsers', 'myCommunityProfiles');
  }
  

  // fetch all users in current community, current community would be pulled from claim
  public static async fetchForCommunity() {
    return super.fetchArray(CommunityUser, 'communityUsers', 'users');
  }

  // filtering 
  public static override findMatching<Obj>(objType: new (init?: any) => Obj, term: string): Promise<Obj[]> {
    return super.findMatching(objType, term, 'communityUsers', 'users/filter');
  }

  // get single sign on token for loggin into Raffle Rocket
  public static async getSSOToken(raffleId?: number, auctionId?: number) {
    try {
      if (raffleId > 0) {
        return await super.fetchUntyped('Authenticating', 'communityUsers', `sso/generate/${raffleId}`, 'text');
      } else if (auctionId > 0) {
        return await super.fetchUntyped('Authenticating', 'communityUsers', `sso/generateForAuction/${auctionId}`, 'text');
      } else if (auctionId == 0) {
        return await super.fetchUntyped('Authenticating', 'communityUsers', `sso/generateForAuction`, 'text');
      } else {
        return await super.fetchUntyped('Authenticating', 'communityUsers', `sso/generate`, 'text');
      }
    } catch (error) {
      throw error;
    }
  }

  // get single sign on token for the copy-raffle-via-CBS flow (identity-only,
  // does not grant access to the source raffle - see cbsAPI's CBS-02 endpoint)
  public static async getSSOTokenForRaffleCopy(raffleId: number) {
    try {
      return await super.fetchUntyped('Authenticating', 'communityUsers', `sso/generate/copyraffle/${raffleId}`, 'text');
    } catch (error) {
      throw error;
    }
  }

  async fetchSurveyResponses(): Promise<SurveyResponse[]> {
    return await Entity.factory.fetchObj(SurveyResponse, 'surveyresponses', `foruser/${this.userId}`);
  }

  private async parseSpots() {
    this.upcomingEvents.forEach(upcomingEvent => {
        // cross reference event with registration counts to get remaining spots]
        if (upcomingEvent != null && this.eventRegs.length > 0 && upcomingEvent.maxAttendees > 0) {
            if (upcomingEvent.eventType === CbsEventType.Bundled) {
                // for bundled events, availability is not on the instance but the event
                const regs = this.eventRegs.find(er => er.e === upcomingEvent.eventId && er.i == null);
                upcomingEvent.remainingSpots = Math.max(0, upcomingEvent.maxAttendees - (regs != null ? regs.c : 0));
            } else {
                const instId = upcomingEvent.instanceId != null ? upcomingEvent.instanceId :
                    (upcomingEvent.eventInstances.length > 0 ? upcomingEvent.eventInstances[0].eventInstanceId : null);
                const regs = this.eventRegs.find(er => er.e === upcomingEvent.eventId && er.i === instId);
                upcomingEvent.remainingSpots = Math.max(0, upcomingEvent.maxAttendees - (regs != null ? regs.c : 0));
            }
        } else {
            // always lots of spots if no maxAttendees specified
            upcomingEvent.remainingSpots = 99;
        }
    });
}
}

// list of registration counts by e(eventId), i(eventInstanceId) and c(count)
export class EventRegs {
  e: number;
  i: number;
  c: number
}